(function () {
  "use strict";

  if (window.ShippingMilestoneInitialized) return;
  window.ShippingMilestoneInitialized = true;

  const CONFIG = {
    UPDATE_DELAY: 150,
    CONFIG_WAIT_ATTEMPTS: 30,
    CONFIG_WAIT_INTERVAL: 200,
  };

  let state = {
    currentMilestone: null,
    appliedDiscountCode: null,
  };

  const ROOT_PROGRESS_VAR = "--shipping-milestone-progress";

  let lastCartData = null;
  let refreshTimer = null;
  let isFetchingCart = false;
  let isApplyingDiscount = false;
  let cartUpdateUnsubscriber = null;
  let dynamicStyleApplied = false;
  let fetchPatched = false;

  function getConfigElement() {
    return document.querySelector(".shipping-milestone-config");
  }

  function getWrapper() {
    return document.querySelector(".shipping-milestone-wrapper");
  }

  function getMilestones(wrapper) {
    const root = wrapper || getWrapper();
    if (!root) return [];

    return Array.from(
      root.querySelectorAll(".milestone-marker[data-threshold]"),
    )
      .map((marker) => ({
        threshold: parseInt(marker.dataset.threshold, 10),
        discount: parseInt(marker.dataset.discount, 10) || 0,
        code: (marker.dataset.code || "").trim(),
      }))
      .filter(
        (milestone) =>
          milestone.threshold > 0 && !Number.isNaN(milestone.threshold),
      )
      .sort((a, b) => a.threshold - b.threshold);
  }

  function getConfig() {
    const el = getConfigElement();
    if (!el) return null;

    return {
      enabled: el.dataset.enabled === "true",
      showConfetti: el.dataset.showConfetti === "true",
      colors: {
        wrapperGradientStart: el.dataset.wrapperGradientStart,
        wrapperGradientEnd: el.dataset.wrapperGradientEnd,
        progressBarStart: el.dataset.progressBarStart,
        progressBarEnd: el.dataset.progressBarEnd,
        milestoneAchievedColor: el.dataset.milestoneAchievedColor,
      },
    };
  }

  function normalizeCode(code) {
    return typeof code === "string" ? code.trim().toUpperCase() : "";
  }

  function getCurrentMilestone(total, milestones) {
    let achieved = null;

    for (const milestone of milestones) {
      if (total >= milestone.threshold) {
        achieved = milestone;
      } else {
        break;
      }
    }

    return achieved;
  }

  function getMilestoneCartTotal(cartData) {
    if (!cartData) return 0;
    const subtotal = Number(cartData.items_subtotal_price);
    if (subtotal > 0) return subtotal;
    return Number(cartData.total_price || 0);
  }

  function getCartAttribute(cartData, key) {
    if (
      !cartData ||
      !cartData.attributes ||
      typeof cartData.attributes !== "object"
    ) {
      return null;
    }

    const value = cartData.attributes[key];
    return typeof value === "string" && value.trim() ? value.trim() : null;
  }

  function getMarkerSlotPosition(index, count) {
    if (count <= 0) return 0;
    if (count === 1) return 100;
    if (index === count - 1) return 100;
    return ((index + 0.5) / count) * 100;
  }

  function calculateProgress(total, milestones) {
    if (!milestones.length || total <= 0) return 0;

    const n = milestones.length;
    const lastThreshold = milestones[n - 1].threshold;

    if (total >= lastThreshold) return 100;

    const firstThreshold = milestones[0].threshold;
    if (total < firstThreshold) {
      const target = getMarkerSlotPosition(0, n);
      return (total / firstThreshold) * target;
    }

    for (let i = 0; i < n - 1; i++) {
      const low = milestones[i].threshold;
      const high = milestones[i + 1].threshold;
      if (total >= low && total < high) {
        const pLow = getMarkerSlotPosition(i, n);
        const pHigh = getMarkerSlotPosition(i + 1, n);
        const ratio = (total - low) / (high - low);
        return pLow + ratio * (pHigh - pLow);
      }
    }

    return 100;
  }

  function measureBarProgress(total, wrapper) {
    const track = wrapper.querySelector(".milestone-progress-track");
    const markers = [
      ...wrapper.querySelectorAll(".milestone-marker[data-threshold]"),
    ];
    if (!track || !markers.length) return null;

    const trackRect = track.getBoundingClientRect();
    if (trackRect.width <= 0) return null;

    const positions = markers.map((marker) => {
      const icon = marker.querySelector(".marker-icon") || marker;
      const iconRect = icon.getBoundingClientRect();
      const center = iconRect.left + iconRect.width / 2 - trackRect.left;
      return (center / trackRect.width) * 100;
    });

    const thresholds = markers.map((m) =>
      parseInt(m.dataset.threshold, 10),
    );
    const n = markers.length;

    if (total >= thresholds[n - 1]) return 100;

    if (total < thresholds[0]) {
      return (total / thresholds[0]) * positions[0];
    }

    for (let i = 0; i < n - 1; i++) {
      if (total >= thresholds[i] && total < thresholds[i + 1]) {
        const ratio =
          (total - thresholds[i]) / (thresholds[i + 1] - thresholds[i]);
        return positions[i] + ratio * (positions[i + 1] - positions[i]);
      }
    }

    return 100;
  }

  function resolveProgress(total, milestones, wrapper) {
    if (wrapper) {
      const measured = measureBarProgress(total, wrapper);
      if (measured != null && !Number.isNaN(measured)) {
        return Math.min(100, Math.max(0, measured));
      }
    }
    return calculateProgress(total, milestones);
  }

  function setRootProgress(pct) {
    const clamped = Math.min(100, Math.max(0, Number(pct) || 0));
    document.documentElement.style.setProperty(
      ROOT_PROGRESS_VAR,
      `${clamped}%`,
    );
  }

  function resetRootProgress() {
    setRootProgress(0);
  }

  function updateProgressBar(total, milestones, wrapper) {
    const el = wrapper || getWrapper();
    setRootProgress(resolveProgress(total, milestones, el));
  }

  function syncProgressFromWrapper(wrapper) {
    const el = wrapper || getWrapper();
    if (!el) return;

    const pct = parseFloat(el.dataset.progress);
    if (!Number.isNaN(pct)) {
      setRootProgress(pct);
    }

    const achieved = el.dataset.achievedCount;
    if (achieved !== undefined) {
      document.documentElement.setAttribute(
        "data-milestones-achieved",
        achieved && achieved !== "0" ? String(achieved) : "",
      );
    }
  }

  function applyMarkerState(wrapper, total) {
    let achievedCount = 0;

    wrapper.querySelectorAll(".milestone-marker").forEach((marker) => {
      const threshold = parseInt(marker.dataset.threshold, 10);
      const isAchieved = !Number.isNaN(threshold) && total >= threshold;
      const wasAchieved = marker.classList.contains("achieved");

      marker.classList.toggle("achieved", isAchieved);
      if (isAchieved) achievedCount++;

      if (!wasAchieved && isAchieved) {
        marker.classList.add("milestone-pop");
        setTimeout(() => marker.classList.remove("milestone-pop"), 500);
      }
    });

    document.documentElement.setAttribute(
      "data-milestones-achieved",
      achievedCount > 0 ? String(achievedCount) : "",
    );

    const milestones = getMilestones(wrapper);
    const progressPct = resolveProgress(total, milestones, wrapper);
    wrapper.dataset.progress = String(
      Math.round(progressPct * 100) / 100,
    );
    wrapper.dataset.achievedCount = String(achievedCount);
    setRootProgress(progressPct);

    return achievedCount;
  }

  function whenLayoutReady(callback) {
    requestAnimationFrame(() => requestAnimationFrame(callback));
  }

  function applyDynamicColors(colors) {
    if (!colors) return;

    const wrapper = getWrapper();
    if (!wrapper) return;

    if (colors.wrapperGradientStart && colors.wrapperGradientEnd) {
      wrapper.style.background = `linear-gradient(135deg, ${colors.wrapperGradientStart} 0%, ${colors.wrapperGradientEnd} 100%)`;
    }

    const progressBar = wrapper.querySelector(".milestone-progress-bar");
    if (progressBar && colors.progressBarStart && colors.progressBarEnd) {
      progressBar.style.background = `linear-gradient(90deg, ${colors.progressBarStart} 0%, ${colors.progressBarEnd} 100%)`;
    }

    if (!dynamicStyleApplied) {
      const styleEl = document.createElement("style");
      styleEl.id = "milestone-dynamic-style";
      document.head.appendChild(styleEl);
      dynamicStyleApplied = true;
    }

    const styleEl = document.getElementById("milestone-dynamic-style");
    if (styleEl) {
      const achievedColor = colors.milestoneAchievedColor || "#20b2aa";
      styleEl.textContent = `
        .milestone-marker.achieved .marker-icon {
          background: ${achievedColor} !important;
          border-color: ${achievedColor} !important;
          box-shadow: 0 0 0 3px rgba(32, 178, 170, 0.16) !important;
        }
      `;
    }
  }

  function emitCartSyncEvent(cart) {
    document.dispatchEvent(
      new CustomEvent("shippingMilestone:discountSync", {
        detail: {
          cart,
          code: getCartAttribute(cart, "milestone_discount"),
        },
      }),
    );
  }

  function hasDiscountField() {
    return !!(
      document.querySelector('input[name="discount"]') ||
      document.querySelector("#CartDrawer-Discount") ||
      document.querySelector(".cart__discount input") ||
      document.querySelector("[data-discount-input]")
    );
  }

  function applyCartProgress(cartData) {
    const wrapper = getWrapper();
    if (!wrapper) return false;

    const itemCount = Number(cartData?.item_count || 0);

    if (itemCount === 0) {
      resetRootProgress();
      wrapper.dataset.progress = "0";
      wrapper.dataset.achievedCount = "0";
      document.documentElement.setAttribute("data-milestones-achieved", "");
      lastCartData = null;
      state.currentMilestone = null;
      state.appliedDiscountCode = null;
      return true;
    }

    const total = getMilestoneCartTotal(cartData);
    const milestones = getMilestones(wrapper);
    if (!milestones.length) return false;

    lastCartData = cartData;
    updateProgressBar(total, milestones, wrapper);
    applyMarkerState(wrapper, total);
    return true;
  }

  function updateUI(cartData) {
    const wrapper = getWrapper();
    if (!wrapper) return;

    const previousMilestone = state.currentMilestone;
    if (!applyCartProgress(cartData)) return;

    const total = getMilestoneCartTotal(cartData);
    const itemCount = Number(cartData?.item_count || 0);
    if (itemCount === 0) return;

    const config = getConfig();

    whenLayoutReady(() => {
      const liveWrapper = getWrapper();
      if (!liveWrapper) return;

      const liveMilestones = getMilestones(liveWrapper);
      if (!liveMilestones.length) return;

      updateProgressBar(total, liveMilestones, liveWrapper);
      applyMarkerState(liveWrapper, total);

      if (!config || !config.enabled) return;

      applyDynamicColors(config.colors);

      const currentMilestone = getCurrentMilestone(total, liveMilestones);
      const previousThreshold = previousMilestone?.threshold || 0;
      const hasReachedHigherMilestone =
        currentMilestone && currentMilestone.threshold > previousThreshold;

      if (hasReachedHigherMilestone && config.showConfetti && window.confetti) {
        setTimeout(() => {
          window.confetti({
            particleCount: 100,
            spread: 80,
            origin: { x: 0.9, y: 0.5 },
            zIndex: 1999,
          });
        }, 150);
      }

      if (hasDiscountField()) {
        syncDiscountState(cartData, currentMilestone);
      }

      state.currentMilestone = currentMilestone;
    });

    if (config?.enabled) {
      applyDynamicColors(config.colors);
    }
  }

  async function updateCartAttributes(attributes) {
    const response = await fetch("/cart/update.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ attributes }),
      credentials: "same-origin",
    });

    if (!response.ok) throw new Error("Failed to update milestone discount");
    return response.json();
  }

  async function applyDiscountCode(milestone) {
    if (!milestone?.code || isApplyingDiscount) return;

    const milestoneCode = milestone.code.trim();
    if (!milestoneCode) return;

    isApplyingDiscount = true;

    try {
      const updatedCart = await updateCartAttributes({
        milestone_discount: milestoneCode,
        milestone_discount_applied: new Date().toISOString(),
      });
      state.appliedDiscountCode = milestoneCode;
      emitCartSyncEvent(updatedCart);
      updateUI(updatedCart);
    } catch (error) {
      console.error("Failed to apply discount code:", error);
    } finally {
      isApplyingDiscount = false;
    }
  }

  async function clearDiscountCode(codeToClear = "") {
    if (isApplyingDiscount) return;

    const previousCode = codeToClear || state.appliedDiscountCode || "";
    if (!normalizeCode(previousCode)) {
      state.appliedDiscountCode = null;
      state.currentMilestone = null;
      return;
    }

    isApplyingDiscount = true;

    try {
      const updatedCart = await updateCartAttributes({
        milestone_discount: "",
        milestone_discount_applied: "",
      });
      state.appliedDiscountCode = null;
      state.currentMilestone = null;
      emitCartSyncEvent(updatedCart);
      updateUI(updatedCart);
    } catch (error) {
      console.error("Failed to clear discount code:", error);
    } finally {
      isApplyingDiscount = false;
    }
  }

  function syncDiscountState(cartData, currentMilestone) {
    if (isApplyingDiscount) return;

    const cartCode = getCartAttribute(cartData, "milestone_discount");
    const appliedCode = cartCode || state.appliedDiscountCode;
    const targetCode = currentMilestone?.code || null;
    const normalizedAppliedCode = normalizeCode(appliedCode);
    const normalizedTargetCode = normalizeCode(targetCode);

    if (normalizedTargetCode) {
      if (normalizedAppliedCode !== normalizedTargetCode) {
        applyDiscountCode(currentMilestone);
      }
      return;
    }

    if (normalizedAppliedCode) {
      clearDiscountCode(appliedCode);
    }
  }

  async function fetchCartAndUpdate() {
    if (isFetchingCart) return;
    isFetchingCart = true;

    try {
      const response = await fetch("/cart.js", { credentials: "same-origin" });
      updateUI(await response.json());
    } catch (error) {
      console.error("Failed to fetch cart:", error);
    } finally {
      isFetchingCart = false;
    }
  }

  function refreshProgress(cartData = null) {
    const wrapper = getWrapper();
    if (wrapper) syncProgressFromWrapper(wrapper);

    if (cartData != null && typeof cartData.total_price !== "undefined") {
      if (wrapper) updateUI(cartData);
      else lastCartData = cartData;
      return;
    }

    fetchCartAndUpdate();
  }

  function handleCartChange(cartData = null, delay = CONFIG.UPDATE_DELAY) {
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(() => refreshProgress(cartData), delay);
  }

  function scheduleRefresh(cartData = null, delay = CONFIG.UPDATE_DELAY) {
    handleCartChange(cartData, delay);
  }

  function isCartApiRequest(url) {
    return (
      url.includes("/cart/change") ||
      url.includes("/cart/add") ||
      url.includes("/cart/update")
    );
  }

  function setupFetchInterceptor() {
    if (typeof window.fetch !== "function") return;
    if (window.fetch.__shippingMilestoneWrapped) return;

    const originalFetch = window.fetch.bind(window);
    function wrappedFetch(...args) {
      const requestUrl =
        typeof args[0] === "string"
          ? args[0]
          : args[0] && typeof args[0].url === "string"
            ? args[0].url
            : "";

      return originalFetch(...args).then((response) => {
        if (!isCartApiRequest(requestUrl)) return response;

        response
          .clone()
          .json()
          .then((data) => {
            if (data && typeof data.total_price !== "undefined") {
              handleCartChange(data, CONFIG.UPDATE_DELAY);
            } else {
              handleCartChange(null, CONFIG.UPDATE_DELAY);
            }
          })
          .catch(() => {
            handleCartChange(null, CONFIG.UPDATE_DELAY);
          });

        return response;
      });
    }

    wrappedFetch.__shippingMilestoneWrapped = true;
    window.fetch = wrappedFetch;
    fetchPatched = true;
  }

  function isMeaningfulMutation(mutations) {
    for (const m of mutations) {
      for (const node of [...m.addedNodes, ...m.removedNodes]) {
        if (node.nodeType !== 1) continue;
        if (node.classList?.contains("ms-zero-overlay")) continue;
        if (node.classList?.contains("ms-gift-overlay")) continue;
        if (
          node.childElementCount === 0 &&
          (node.hasAttribute?.("data-ms-gift-line") ||
            node.hasAttribute?.("data-ms-zero-gift") ||
            node.hasAttribute?.("data-ms-gift-locked"))
        )
          continue;
        return true;
      }
    }
    return false;
  }

  function setupCartDrawerHook() {
    customElements.whenDefined("cart-drawer").then(() => {
      const CartDrawerClass = customElements.get("cart-drawer");
      if (!CartDrawerClass || CartDrawerClass.prototype.__milestonePatched) {
        return;
      }
      CartDrawerClass.prototype.__milestonePatched = true;

      const originalRenderContents = CartDrawerClass.prototype.renderContents;
      CartDrawerClass.prototype.renderContents = function (
        parsedState,
        forceUpdate,
      ) {
        originalRenderContents.call(this, parsedState, forceUpdate);
        const wrapper = document.querySelector(".shipping-milestone-wrapper");
        if (wrapper) syncProgressFromWrapper(wrapper);
        const cartData =
          parsedState && typeof parsedState.total_price !== "undefined"
            ? parsedState
            : null;
        refreshProgress(cartData);
        handleCartChange(cartData, CONFIG.UPDATE_DELAY);
      };

      const cartDrawer = document.querySelector("cart-drawer");
      if (cartDrawer) {
        let drawerMutationTimer = null;
        new MutationObserver((mutations) => {
          if (!isMeaningfulMutation(mutations)) return;
          const wrapper = document.querySelector(".shipping-milestone-wrapper");
          if (wrapper) syncProgressFromWrapper(wrapper);
          clearTimeout(drawerMutationTimer);
          drawerMutationTimer = setTimeout(() => refreshProgress(null), 300);
        }).observe(cartDrawer, { childList: true, subtree: true });
      }
    });
  }

  function setupEventListeners() {
    setupFetchInterceptor();

    if (
      window.PUB_SUB_EVENTS &&
      window.PUB_SUB_EVENTS.cartUpdate &&
      window.subscribe
    ) {
      cartUpdateUnsubscriber = window.subscribe(
        window.PUB_SUB_EVENTS.cartUpdate,
        (event) => {
          handleCartChange(event?.cartData || null, CONFIG.UPDATE_DELAY);
        },
      );
    }

    document.addEventListener("cartUpdated", () => {
      handleCartChange(null, CONFIG.UPDATE_DELAY);
    });

    document.addEventListener("cart:updated", () => {
      handleCartChange(null, CONFIG.UPDATE_DELAY);
    });

    document.addEventListener("shopify:section:load", (event) => {
      const section = event.target;
      const isMilestoneSection =
        section?.querySelector?.(".shipping-milestone-config") ||
        section?.querySelector?.(".shipping-milestone-wrapper") ||
        section?.classList?.contains("shipping-milestone-config");

      if (!isMilestoneSection) return;

      const wrapper = getWrapper();
      if (wrapper) syncProgressFromWrapper(wrapper);
      fetchCartAndUpdate();
    });

    window.addEventListener("resize", () => {
      if (lastCartData) {
        updateUI(lastCartData);
      } else {
        const wrapper = getWrapper();
        if (wrapper) syncProgressFromWrapper(wrapper);
      }
    });
  }

  function waitForConfig(maxAttempts, interval) {
    return new Promise((resolve, reject) => {
      let attempts = 0;
      const check = () => {
        if (getConfigElement()) {
          resolve();
          return;
        }
        attempts += 1;
        if (attempts >= maxAttempts) {
          reject(new Error("Shipping milestone config section not found"));
          return;
        }
        setTimeout(check, interval);
      };
      check();
    });
  }

  function loadConfettiIfNeeded() {
    const config = getConfig();
    if (!config || !config.showConfetti || window.confettiLoaded) return;
    if (window.confetti) {
      window.confettiLoaded = true;
      return;
    }

    const script = document.createElement("script");
    script.src =
      "https://cdn.jsdelivr.net/npm/canvas-confetti@1.6.0/dist/confetti.browser.min.js";
    script.onload = () => {
      window.confettiLoaded = true;
    };
    document.head.appendChild(script);
  }

  async function init() {
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", init, { once: true });
      return;
    }

    setupFetchInterceptor();
    setupCartDrawerHook();
    setupEventListeners();
    refreshProgress(null);

    try {
      await waitForConfig(
        CONFIG.CONFIG_WAIT_ATTEMPTS,
        CONFIG.CONFIG_WAIT_INTERVAL,
      );
      loadConfettiIfNeeded();
      refreshProgress(null);
    } catch (error) {
      console.warn(error.message);
    }

    window.addEventListener("load", () => {
      setupFetchInterceptor();
      refreshProgress(null);
    });
  }

  init();

  window.ShippingMilestone = {
    refresh: () => scheduleRefresh(null, 0),
    getState: () => ({ ...state }),
    destroy: () => {
      clearTimeout(refreshTimer);
      if (typeof cartUpdateUnsubscriber === "function") {
        cartUpdateUnsubscriber();
      }
    },
  };
})();
